import { Box, Button, Typography } from "@mui/joy";
import { AlertCircle, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const AUTOSAVE_SECONDS = 5;

type UnsavedChangesBannerProps = {
    open: boolean;
    syncing?: boolean;
    /** Changes whenever edits happen — restarts the autosave countdown. */
    resetKey?: unknown;
    onSave: () => void;
};

export function UnsavedChangesBanner({
    open,
    syncing = false,
    resetKey,
    onSave,
}: UnsavedChangesBannerProps) {
    const [countdown, setCountdown] = useState(AUTOSAVE_SECONDS);
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;

    useEffect(() => {
        if (!open || syncing) {
            return;
        }

        let remaining = AUTOSAVE_SECONDS;
        setCountdown(remaining);

        const intervalId = window.setInterval(() => {
            remaining -= 1;
            if (remaining > 0) {
                setCountdown(remaining);
                return;
            }

            window.clearInterval(intervalId);
            setCountdown(0);
            onSaveRef.current();
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [open, syncing, resetKey]);

    if (!open) {
        return null;
    }

    const title = syncing
        ? "Saving…"
        : countdown > 0
          ? `Autosaving in ${countdown}`
          : "Autosaving…";

    return (
        <Box
            sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 80,
                zIndex: 1200,
                display: "flex",
                justifyContent: "center",
                px: 2,
                pointerEvents: "none",
            }}
        >
            <Box
                sx={{
                    pointerEvents: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "warning.outlinedBorder",
                    bgcolor: "warning.softBg",
                    boxShadow: "sm",
                }}
            >
                <Box
                    sx={{
                        color: "warning.500",
                        bgcolor: "warning.softActiveBg",
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                    }}
                >
                    {!syncing && countdown > 0 ? (
                        countdown
                    ) : (
                        <AlertCircle size={14} />
                    )}
                </Box>
                <Typography level="body-sm" color="warning" sx={{ whiteSpace: "nowrap" }}>
                    {title}
                </Typography>
                <Button
                    size="sm"
                    variant="solid"
                    color="warning"
                    loading={syncing}
                    onClick={onSave}
                    startDecorator={<Save size={14} />}
                    sx={{
                        borderRadius: "4px",
                        flexShrink: 0,
                        minHeight: 28,
                        px: 1.25,
                        ml: 1.5,
                        "--Button-gap": "4px",
                    }}
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
}
